<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Switch from 'dashboard/components-next/switch/Switch.vue';
const ESTADO_IA_ATTRIBUTE = 'estado_ia';
const ESTADO_IA_ON = 'On';
const ESTADO_IA_OFF = 'Off';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const isSaving = ref(false);
const isEstadoIaOn = ref(false);

const currentChat = computed(() => getters.getSelectedChat.value);
const activeConversationId = computed(
  () => getters.getActiveConversationId.value
);

const conversationAttributes = computed(() =>
  getters['attributes/getAttributesByModel'].value('conversation_attribute')
);

const showSwitch = computed(() =>
  conversationAttributes.value.some(
    attribute => attribute.attribute_key === ESTADO_IA_ATTRIBUTE
  )
);

const estadoIaLabel = computed(() =>
  isEstadoIaOn.value
    ? t('CONVERSATION.HEADER.ESTADO_IA.ON')
    : t('CONVERSATION.HEADER.ESTADO_IA.OFF')
);

const syncActiveConversationIfNeeded = async () => {
  const chat = currentChat.value;
  if (!chat?.contact_inbox_id) return;
  if (chat.active_conversation_id && chat.active_conversation_id !== chat.id) {
    await store.dispatch('syncActiveConversationDetails', chat.id);
  }
};

watch(
  [
    () => currentChat.value?.custom_attributes?.[ESTADO_IA_ATTRIBUTE],
    () => activeConversationId.value,
  ],
  ([estadoIa]) => {
    isEstadoIaOn.value = estadoIa !== ESTADO_IA_OFF;
  },
  { immediate: true }
);

watch(
  [() => currentChat.value?.id, () => activeConversationId.value],
  () => {
    syncActiveConversationIfNeeded();
  },
  { immediate: true }
);

const onEstadoIaChange = async () => {
  if (isSaving.value || !activeConversationId.value) return;

  const estadoIa = isEstadoIaOn.value ? ESTADO_IA_ON : ESTADO_IA_OFF;
  const updatedAttributes = {
    ...(currentChat.value.custom_attributes || {}),
    [ESTADO_IA_ATTRIBUTE]: estadoIa,
  };

  isSaving.value = true;
  try {
    await store.dispatch('updateCustomAttributes', {
      conversationId: activeConversationId.value,
      customAttributes: updatedAttributes,
    });
    useAlert(t('CUSTOM_ATTRIBUTES.FORM.UPDATE.SUCCESS'));
  } catch {
    isEstadoIaOn.value = !isEstadoIaOn.value;
    useAlert(t('CUSTOM_ATTRIBUTES.FORM.UPDATE.ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div v-if="showSwitch" class="flex items-center gap-2 shrink-0">
    <span class="text-sm font-medium text-n-slate-12">
      {{ t('CONVERSATION.HEADER.ESTADO_IA.LABEL') }}
    </span>
    <Switch v-model="isEstadoIaOn" @change="onEstadoIaChange" />
    <span class="text-xs text-n-slate-11 min-w-[2rem]">
      {{ estadoIaLabel }}
    </span>
  </div>
</template>
